import React, { useEffect, useState } from 'react';
import { Building2, Plus, Truck, Warehouse, Archive, ArchiveRestore, Pencil } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { inv, LOCATION_TYPES } from '../../lib/invApi';

const blank = () => ({ name: '', type: 'shop', code: '', vehicle_id: '', assigned_user_id: '', address: '', description: '' });

const InventoryLocations = () => {
  const [locs, setLocs] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [showArchived]);
  const refresh = async () => { const { data } = await inv.get('/locations', { include_archived: showArchived }); setLocs(data); };

  const openCreate = () => { setEditing(null); setForm(blank()); setOpen(true); };
  const openEdit = (l) => { setEditing(l); setForm({ ...blank(), ...l, vehicle_id: l.vehicle_id || '', assigned_user_id: l.assigned_user_id || '' }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form };
      ['vehicle_id', 'assigned_user_id', 'address', 'description', 'code'].forEach((k) => { if (!payload[k]) delete payload[k]; });
      if (editing) await inv.put(`/locations/${editing.id}`, payload);
      else await inv.post('/locations', payload);
      toast.success(editing ? 'Updated' : 'Created');
      setOpen(false); refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };

  const archive = async (l) => {
    if (!window.confirm(`Archive "${l.name}"? Blocks if non-zero stock remains.`)) return;
    try { await inv.patch(`/locations/${l.id}/archive`); toast.success('Archived'); refresh(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const iconOf = (t) => t === 'van' ? Truck : t === 'warehouse' ? Warehouse : Building2;

  return (
    <div data-testid="inv-locations-page" className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-1 flex items-center gap-3"><Building2 className="w-7 h-7 text-[#D4AF37]" /> Locations</h2>
          <p className="text-[#A3A3A3]">Shop, vans, warehouses, trailers, jobsites — any bucket that holds stock.</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-white text-sm cursor-pointer">
            <input data-testid="inv-locs-show-archived" type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="accent-[#D4AF37]" />
            Show archived
          </label>
          <Button data-testid="inv-loc-new-btn" onClick={openCreate} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"><Plus className="w-4 h-4 mr-2" /> New Location</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {locs.length === 0 ? (
          <div className="col-span-full bg-[#141414] border border-[#262626] rounded-md p-6 text-center text-[#525252] text-sm">No locations yet. Start with "Shop" and your vans.</div>
        ) : locs.map((l) => {
          const Icon = iconOf(l.type);
          return (
            <div key={l.id} data-testid={`inv-loc-row-${l.id}`} className={`bg-[#141414] border ${l.archived ? 'border-[#262626] opacity-60' : 'border-[#262626] hover:border-[#D4AF37]'} rounded-md p-4`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#D4AF37]/10 rounded-md flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-[#D4AF37]" /></div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium truncate">{l.name}</p>
                  <p className="text-[#A3A3A3] text-xs uppercase">{l.type}</p>
                </div>
                {l.archived && <Badge className="bg-amber-900/30 text-amber-400 border-amber-700 text-[10px]">ARCHIVED</Badge>}
              </div>
              {l.address && <p className="text-[#525252] text-xs mt-2 truncate">{l.address}</p>}
              <div className="mt-3 flex items-center justify-between rounded-md bg-[#0A0A0A] border border-[#262626] px-3 py-2">
                <span className="text-[#A3A3A3] text-[11px] uppercase tracking-wider">Inventory Value</span>
                <span data-testid={`inv-loc-value-${l.id}`} className="text-white font-semibold text-sm">
                  {`$${Number(l.inventory_value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                </span>
              </div>
              <div className="flex items-center justify-end gap-1 mt-3">
                <Button data-testid={`inv-loc-edit-${l.id}`} onClick={() => openEdit(l)} size="sm" variant="outline" className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 h-9 px-2.5"><Pencil className="w-4 h-4" /></Button>
                {!l.archived && (
                  <Button data-testid={`inv-loc-archive-${l.id}`} onClick={() => archive(l)} size="sm" variant="outline" className="border-[#262626] text-amber-400 hover:bg-amber-900/20 h-9 px-2.5"><Archive className="w-4 h-4" /></Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="inv-loc-dialog" className="bg-[#141414] border-[#262626] text-white max-w-md">
          <DialogHeader><DialogTitle>{editing ? `Edit — ${editing.name}` : 'New Location'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Name *</Label><Input data-testid="inv-loc-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="inv-loc-form-type" className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">
                  {LOCATION_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Address (warehouses)</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
            <div><Label className="text-xs">Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button onClick={() => setOpen(false)} variant="outline" className="border-[#262626]">Cancel</Button>
            <Button data-testid="inv-loc-save" onClick={save} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">{editing ? 'Save' : 'Create'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InventoryLocations;
